/**
 * Astro output mode.
 *
 * @see https://docs.astro.build/en/reference/configuration-reference/#output
 */
export const AstroOutput = {
  STATIC: "static",
  SERVER: "server",
  HYBRID: "hybrid",
} as const;

export type AstroOutput = (typeof AstroOutput)[keyof typeof AstroOutput];

/**
 * Declarative spec for an Astro integration or adapter import.
 *
 * Rendered into `astro.config.mjs` as an import plus an invocation in
 * the `integrations` array (or as the `adapter` field).
 */
export interface AstroIntegrationSpec {
  /**
   * Local binding used in the generated config, e.g. "react".
   */
  readonly name: string;

  /**
   * Module to import from, e.g. `@astrojs/react`.
   */
  readonly importPath: string;

  /**
   * Raw argument expression passed to the integration call.
   *
   * Written verbatim inside the parentheses, e.g. `"{ include: ['**\/*.tsx'] }"`.
   * When omitted the integration is invoked with no arguments.
   */
  readonly args?: string;

  /**
   * When true, use a default import (`import name from ...`).
   * When false or omitted, use a named import (`import { name } from ...`).
   *
   * @default true
   */
  readonly defaultImport?: boolean;
}

/**
 * Options controlling the rendered `astro.config.mjs`.
 */
export interface AstroConfigOptions {
  /**
   * Site URL (used for canonical URLs, sitemaps, RSS).
   */
  readonly site?: string;

  /**
   * Base path the site is deployed under.
   */
  readonly base?: string;

  /**
   * Output mode.
   *
   * @default AstroOutput.STATIC
   */
  readonly output?: AstroOutput;

  /**
   * Integrations rendered into the `integrations` array.
   */
  readonly integrations?: Array<AstroIntegrationSpec>;

  /**
   * SSR adapter rendered into the `adapter` field.
   */
  readonly adapter?: AstroIntegrationSpec;
}
